import { useAtomValue } from 'jotai';
import { createElement, Fragment, type ReactNode } from 'react';
import { activeImportMarkerAtom } from '@/atoms/import';
import { versionAtom } from '@/atoms/version';
import { projectIdAtom } from '@/atoms/workspace';
import { usePermissions } from '@/hooks/usePermissions';

export class ReadOnlyMutationError extends Error {
    constructor(message = 'Mutation blocked: application is in read-only mode (viewing a version)') {
        super(message);
        this.name = 'ReadOnlyMutationError';
    }
}

const DISABLED_PROPS = Object.freeze({ disabled: true } as const);
const ENABLED_PROPS = Object.freeze({ disabled: false } as const);

function gateProps(isReadOnly: boolean) {
    const guardProps = isReadOnly ? DISABLED_PROPS : ENABLED_PROPS;
    const guard = <T extends (...args: unknown[]) => unknown>(fn: T): T => {
        if (isReadOnly) {
            return ((..._args: unknown[]) => {}) as unknown as T;
        }
        return fn;
    };
    return { guardProps, guard } as const;
}

/**
 * The workspace is FROZEN: a version is being viewed, or this project is
 * being rewritten by an import. Nothing may be written by anyone — members
 * and company info included. The role-agnostic half of {@link useReadOnlyGate};
 * the screens that are not about project content (employees, projects list,
 * company card, versions, export) read this one, so a company admin — who
 * never edits content — still manages members.
 */
export function useVersionGate() {
    const version = useAtomValue(versionAtom);
    const activeImportMarker = useAtomValue(activeImportMarkerAtom);
    const projectId = useAtomValue(projectIdAtom);
    // Only the project the import is actually rewriting goes read-only —
    // importing project A must not lock editing on project B.
    const importInProgress = activeImportMarker !== null && activeImportMarker.project_id === projectId;
    const isReadOnly = version !== '' || importInProgress;

    return { isReadOnly, importInProgress, ...gateProps(isReadOnly) } as const;
}

/**
 * Project CONTENT is read-only for the signed-in user: the workspace is frozen
 * ({@link useVersionGate}), or their role in the project is not the author
 * level (#1836 — editors and admins read the script, the plan, the lines, the
 * characters…). Everything that edits content reads this gate. Notes are the
 * one carve-out and have their own capability (`FountainBlockCapabilities.notes`).
 */
export function useReadOnlyGate() {
    const frozen = useVersionGate();
    const { canEditContent } = usePermissions();
    const isReadOnly = frozen.isReadOnly || !canEditContent;

    return { isReadOnly, importInProgress: frozen.importInProgress, ...gateProps(isReadOnly) } as const;
}

export function WriteOnly({ children }: { children: ReactNode }) {
    const { isReadOnly } = useReadOnlyGate();
    if (isReadOnly) return null;
    return createElement(Fragment, null, children);
}
